import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import "leaflet-polylinedecorator"

// Connects to data-controller="map"
export default class extends Controller {
  static targets = ["nextPoiButton"]

  static values = {
    startLat: Number,
    startLon: Number,
    geojson: String,
    pois: String,
    phase: String,
    showZoom: { type: Boolean, default: true },
    currentPoiIndex: { type: Number, default: 0 },
    fitBoundsPoiIndexes: Array,
    highlightSegmentIndexes: Array,
    itineraryOverview: { type: Boolean, default: false },
    highlightSegmentColor: { type: String, default: "red" }
  }

  connect() {
    const lat = this.startLatValue
    const lon = this.startLonValue
    const showZoom = this.showZoomValue !== undefined ? this.showZoomValue : true
    const currentPoiIndex = this.currentPoiIndexValue !== undefined ? this.currentPoiIndexValue : 0
    if (!lat || !lon) return

    this._markerTimeouts = []
    this._buildRouteTimeout = null
    this._dynamicLayers = []
    this._poiLayersByKey = new Map()
    this._routeLatLngs = []
    this._nextPoiIndex = 0
    this._activePoiIndex = null
    this._handleMapPatch = (event) => this.applyMapPatch(event)

    this.map = L.map(this.element, { zoomControl: showZoom }).setView([lat, lon], 14)
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      attribution:
        '&copy; <a href="https://carto.com/attributions">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.map)

    if (showZoom && !this.map.zoomControl) {
      L.control.zoom().addTo(this.map)
    }

    this.addPhaseScanLayer(lat, lon)
    this.renderRouteIfAny()
    this.renderPois(currentPoiIndex)
    this.addUserLocation()
    document.addEventListener("loading-map:patch", this._handleMapPatch)
    document.addEventListener("activate-poi", this.activatePoi.bind(this))
  }

  disconnect() {
    this.clearTimers()
    document.removeEventListener("loading-map:patch", this._handleMapPatch)
    if (this._geoWatchId) navigator.geolocation.clearWatch(this._geoWatchId)
    if (this.map) this.map.remove()
  }

  activatePoi(e) {
    const poi = this.poiFromEvent(e)
    this.centerMapOnPoi(poi)
    this.highlightPoiMarker(poi)
  }

  poiFromEvent(event) {
    const poiId = event.detail?.poiId
    if (poiId === undefined || poiId === null) return null

    return this.poisFromValue().find((poi) => String(poi.id) === String(poiId))
  }

  poisFromValue() {
    if (!this.hasPoisValue || !this.poisValue) return []

    try {
      return JSON.parse(this.poisValue)
    } catch (error) {
      console.warn("[map_controller] Erreur parsing POIs:", error)
      return []
    }
  }

  centerMapOnPoi(poi) {
    if (!poi?.lat || !poi?.lon || !this.map) return

    this.map.setView([poi.lat, poi.lon], this.map.getZoom(), { animate: true })
  }

  highlightPoiMarker(poi) {
    if (!poi) return

    const pois = this.poisFromValue()
    const activeIndex = pois.findIndex((candidate) => String(candidate.id) === String(poi.id))
    if (activeIndex < 0) return

    this._activePoiIndex = activeIndex
    const numberedMarkers = this.shouldUseNumberedMarkers(pois.length)
    this._poiLayersByKey.forEach((marker, key) => {
      const markerIndex = pois.findIndex((candidate, index) => this.poiKeyFor(candidate, index) === key)
      if (markerIndex >= 0 && numberedMarkers) {
        marker.setIcon(this.buildNumberedPoiIcon(markerIndex, this._activePoiIndex))
      }
    })
  }

  adjustZoomBetweenPois(previousPoi, poi) {
    if (!previousPoi?.lat || !previousPoi?.lon || !poi?.lat || !poi?.lon || !this.map) return false

    this.map.fitBounds(
      L.latLngBounds(
        [previousPoi.lat, previousPoi.lon],
        [poi.lat, poi.lon]
      ),
      {
        padding: [2, 2],
        maxZoom: 100,
        animate: true
      }
    )

    return true
  }

  activateNextPoi() {
    const poi = this.nextPoi()
    if (!poi) return

    const previousPoi = this.previousPoiFor(poi)
    this.activatePoi({ detail: { poiId: poi.id } })
    this.adjustZoomBetweenPois(previousPoi, poi)
    this.updateNextPoiButton()
  }

  nextPoi() {
    const poi = this.poisFromValue()[this._nextPoiIndex]
    if (!poi) return null

    this._nextPoiIndex += 1
    return poi
  }

  previousPoiFor(poi) {
    if (this._nextPoiIndex === 1) {
      return { lat: this.startLatValue, lon: this.startLonValue }
    }

    const pois = this.poisFromValue()
    return pois[this._nextPoiIndex - 2] || poi
  }

  updateNextPoiButton() {
    if (this.hasNextPoiButtonTarget) {
      this.nextPoiButtonTarget.textContent = this._nextPoiIndex
    }
  }

  clearTimers() {
    if (this._buildRouteTimeout) clearTimeout(this._buildRouteTimeout)
    this._markerTimeouts.forEach((id) => clearTimeout(id))
    this._markerTimeouts = []
    this._buildRouteTimeout = null
  }

  clearPoiLayers() {
    this._poiLayersByKey.forEach((layer) => {
      if (this.map && this.map.hasLayer(layer)) this.map.removeLayer(layer)
    })
    this._poiLayersByKey.clear()
  }

  addPhaseScanLayer(lat, lon) {
    if (this.phaseValue !== "scan_area") return
    this.addDynamicLayer(
      L.circle([lat, lon], {
      radius: 450,
      color: "#2b6cee",
      weight: 2,
      opacity: 0.45,
      fillColor: "#2b6cee",
      fillOpacity: 0.06,
      dashArray: "6 8"
      }).addTo(this.map)
    )
  }

  renderRouteIfAny() {
    if (!this.hasGeojsonValue || !this.geojsonValue) return
    try {
      const geojson = JSON.parse(this.geojsonValue)
      this._routeLatLngs = extractLineCoords(geojson)
      this.renderMainRoute(geojson)
      this.renderRouteDirectionIfAny()
      this.renderHighlightedSegmentIfAny(geojson)
    } catch (e) {
      console.warn("[map_controller] Erreur parsing geojson:", e)
      this._routeLatLngs = []
    }
  }

  renderMainRoute(geojson) {
    if (this.phaseValue === "build_route") {
      const pendingCasing = L.geoJSON(geojson, {
        style: {
          color: "#ffffff",
          weight: 8,
          opacity: 0.72,
          lineCap: "round",
          lineJoin: "round"
        }
      }).addTo(this.map)
      const pendingCore = L.geoJSON(geojson, {
        style: {
          color: "#2b6cee",
          weight: 4.4,
          opacity: 0.82,
          dashArray: "7 10",
          lineCap: "round",
          lineJoin: "round"
        }
      }).addTo(this.map)
      this.addDynamicLayer(pendingCasing)
      this.addDynamicLayer(pendingCore)
      this._buildRouteTimeout = setTimeout(() => {
        if (!this.map) return
        this.map.removeLayer(pendingCasing)
        this.map.removeLayer(pendingCore)
        this.addMainRouteLayers(
          L.geoJSON(geojson, {
            style: {
              color: "#ffffff",
              weight: 8,
              opacity: 0.9,
              lineCap: "round",
              lineJoin: "round"
            }
          }),
          L.geoJSON(geojson, {
            style: {
              color: "#2b6cee",
              weight: 4.8,
              opacity: 0.96,
              lineCap: "round",
              lineJoin: "round"
            }
          })
        )
      }, 1100)
      return
    }

    const coreStyle =
      this.phaseValue === "save_and_finalize"
        ? { color: "#2b6cee", weight: 4.8, opacity: 0.96, lineCap: "round", lineJoin: "round" }
        : { color: "#2f6cd8", weight: 4.4, opacity: 0.9, smoothFactor: 1.5, lineCap: "round", lineJoin: "round" }

    const casingStyle =
      this.phaseValue === "save_and_finalize"
        ? { color: "#ffffff", weight: 8, opacity: 0.9, lineCap: "round", lineJoin: "round" }
        : { color: "#ffffff", weight: 7.2, opacity: 0.85, smoothFactor: 1.5, lineCap: "round", lineJoin: "round" }

    this.addMainRouteLayers(
      L.geoJSON(geojson, { style: casingStyle }),
      L.geoJSON(geojson, { style: coreStyle })
    )
  }

  addMainRouteLayers(casingLayer, coreLayer) {
    if (!casingLayer || !coreLayer) return
    this.addDynamicLayer(casingLayer.addTo(this.map))
    this.addDynamicLayer(coreLayer.addTo(this.map))
  }

  renderRouteDirectionIfAny() {
    if (this.hasSegmentFocus()) return
    if (!Array.isArray(this._routeLatLngs) || this._routeLatLngs.length < 2) return

    const latlngs = this._routeLatLngs.map(([lat, lon]) => L.latLng(lat, lon))
    let length = 0
    for (let i = 1; i < latlngs.length; i++) length += latlngs[i - 1].distanceTo(latlngs[i])

    const arrowCount = Math.max(2, Math.min(8, Math.round(length / 280)))
    const repeat = length / arrowCount
    this.addDynamicLayer(
      L.polylineDecorator(this._routeLatLngs, {
        patterns: [
          {
            offset: repeat / 2,
            repeat: repeat,
            symbol: buildArrowHead(8, "#2b6cee", "#ffffff")
          }
        ]
      }).addTo(this.map)
    )
  }

  routeAndPoiFitLatLngs(poiLatLngs, pois, fitBoundsIndexes = null) {
    if (fitBoundsIndexes && fitBoundsIndexes.length > 0) {
      return fitBoundsIndexes
        .map((i) => pois[i])
        .filter((poi) => poi && poi.lat && poi.lon)
        .map((poi) => [poi.lat, poi.lon])
    }

    const routeLatLngs = Array.isArray(this._routeLatLngs) ? this._routeLatLngs : []
    return [ ...poiLatLngs, ...routeLatLngs ]
  }

  fitPaddingForMap() {
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches
    if (!isMobile) {
      return {
        paddingTopLeft: [44, 20],
        paddingBottomRight: [20, 20]
      }
    }

    const isLoadingMap = Boolean(this.element.closest("#loading-map"))
    if (isLoadingMap) {
      return {
        paddingTopLeft: [64, 20],
        paddingBottomRight: [20, 140]
      }
    }

    if (this.hasSegmentFocus()) {
      return {
        paddingTopLeft: [56, 20],
        paddingBottomRight: [20, 32]
      }
    }

    return {
      paddingTopLeft: [76, 20],
      paddingBottomRight: [20, 280]
    }
  }

  renderHighlightedSegmentIfAny(geojson) {
    if (
      !this.hasHighlightSegmentIndexesValue ||
      !Array.isArray(this.highlightSegmentIndexesValue) ||
      this.highlightSegmentIndexesValue.length !== 2 ||
      !this.hasPoisValue ||
      !this.poisValue
    ) {
      return
    }

    const pois = JSON.parse(this.poisValue)
    const [startIdx, endIdx] = this.highlightSegmentIndexesValue.map(Number)
    if (
      !pois[startIdx] ||
      !pois[endIdx] ||
      !pois[startIdx].lat ||
      !pois[startIdx].lon ||
      !pois[endIdx].lat ||
      !pois[endIdx].lon
    ) {
      return
    }

    const coords = extractLineCoords(geojson)
    if (coords.length <= 1) return

    const startPoi = [pois[startIdx].lat, pois[startIdx].lon]
    const endPoi = [pois[endIdx].lat, pois[endIdx].lon]
    const i1 = findClosestIndex(coords, startPoi)
    const i2 = findClosestIndex(coords, endPoi)
    const seg = i1 < i2 ? coords.slice(i1, i2 + 1) : coords.slice(i2, i1 + 1)

    this.addDynamicLayer(
      L.polyline(seg, {
      color: "#fff",
      weight: 7,
      opacity: 0.95,
      smoothFactor: 1.5,
      lineCap: "round",
      lineJoin: "round"
      }).addTo(this.map)
    )
    this.addDynamicLayer(
      L.polyline(seg, {
      color: "#2196f3",
      weight: 4.5,
      opacity: 0.95,
      smoothFactor: 1.5,
      lineCap: "round",
      lineJoin: "round"
      }).addTo(this.map)
    )

    const latlngs = seg.map(([pLat, pLng]) => L.latLng(pLat, pLng))
    let length = 0
    for (let i = 1; i < latlngs.length; i++) length += latlngs[i - 1].distanceTo(latlngs[i])
    const maxArrows = 4
    const minArrows = 1
    const arrowCount = Math.max(minArrows, Math.min(maxArrows, Math.round(length / 80)))
    let patterns = []

    if (arrowCount === 1) {
      patterns = [
        {
          offset: "50%",
          repeat: 0,
          symbol: buildArrowHead()
        }
      ]
    } else {
      const repeat = length / arrowCount
      patterns = [
        {
          offset: repeat / 2,
          repeat: repeat,
          symbol: buildArrowHead()
        }
      ]
    }
    this.addDynamicLayer(L.polylineDecorator(seg, { patterns: patterns }).addTo(this.map))
  }

  renderPois(currentPoiIndex, options = {}) {
    if (!this.hasPoisValue || !this.poisValue) return
    try {
      const pois = JSON.parse(this.poisValue)
      const replaceExisting = options.replaceExisting !== false
      const numberedMarkers = this.shouldUseNumberedMarkers(pois.length)
      const markerDelay = this.markerDelayForPhase(pois.length)
      if (replaceExisting) this.clearPoiLayers()
      let fitBoundsIndexes = null
      if (this.hasFitBoundsPoiIndexesValue && this.fitBoundsPoiIndexesValue && Array.isArray(this.fitBoundsPoiIndexesValue)) {
        fitBoundsIndexes = this.fitBoundsPoiIndexesValue.map(Number)
      }

      const poiLatLngs = []
      pois.forEach((poi, idx) => {
        if (!poi.lat || !poi.lon) return
        poiLatLngs.push([poi.lat, poi.lon])
        const poiKey = this.poiKeyFor(poi, idx)

        const timeoutId = setTimeout(() => {
          if (!this.map) return
          const marker = this.createPoiMarker(poi, idx, currentPoiIndex, numberedMarkers)
          this._poiLayersByKey.set(poiKey, marker)
        }, idx * markerDelay)

        this._markerTimeouts.push(timeoutId)
      })

      const fitLatLngs = this.routeAndPoiFitLatLngs(poiLatLngs, pois, fitBoundsIndexes)
      this.fitMapToLatLngs(fitLatLngs)
    } catch (e) {
      console.warn("[map_controller] Erreur parsing pois:", e)
    }
  }

  shouldUseNumberedMarkers(poisCount = 0) {
    return this.itineraryOverviewValue || this.hasSegmentFocus()
  }

  hasSegmentFocus() {
    return (
      this.hasHighlightSegmentIndexesValue &&
      Array.isArray(this.highlightSegmentIndexesValue) &&
      this.highlightSegmentIndexesValue.length === 2
    )
  }

  poiKeyFor(poi, idx = 0) {
    if (poi && poi.id !== undefined && poi.id !== null) return `id:${poi.id}`
    return `coord:${poi?.lat}:${poi?.lon}:${idx}`
  }

  fitMapToLatLngs(latLngs) {
    if (!Array.isArray(latLngs) || latLngs.length === 0) return
    const duration = this.fitBoundsDurationForPhase()
    const { paddingTopLeft, paddingBottomRight } = this.fitPaddingForMap()
    this.map.fitBounds(L.latLngBounds(latLngs), {
      paddingTopLeft,
      paddingBottomRight,
      animate: true,
      duration: duration,
      easeLinearity: 0.15
    })
  }

  markerDelayForPhase(poisCount) {
    const count = Math.max(Number(poisCount) || 0, 1)
    let targetTotalMs = 0
    let minDelayMs = 0
    let maxDelayMs = 0

    if (this.phaseValue === "scan_area" || this.phaseValue === "refine_selection" || this.phaseValue === "enrich_context") {
      // Faster early stages: keep total marker reveal under ~1.8s.
      targetTotalMs = 1800
      minDelayMs = 18
      maxDelayMs = 65
    } else if (this.phaseValue === "build_route" || this.phaseValue === "save_and_finalize") {
      // Slower end stages: make final reveal feel more deliberate.
      targetTotalMs = 2200
      minDelayMs = 45
      maxDelayMs = 130
    } else {
      return 0
    }

    const rawDelay = Math.round(targetTotalMs / count)
    return Math.max(minDelayMs, Math.min(maxDelayMs, rawDelay))
  }

  fitBoundsDurationForPhase() {
    if (this.phaseValue === "scan_area" || this.phaseValue === "refine_selection" || this.phaseValue === "enrich_context") {
      return 1.0
    }
    if (this.phaseValue === "build_route") return 1.6
    if (this.phaseValue === "save_and_finalize") return 2.2
    return 1.4
  }

  patchPoisInPlace(nextPois, currentPoiIndex) {
    const nextKeys = new Set(nextPois.map((poi, idx) => this.poiKeyFor(poi, idx)))
    this._poiLayersByKey.forEach((layer, key) => {
      if (nextKeys.has(key)) return
      if (this.map && this.map.hasLayer(layer)) this.map.removeLayer(layer)
      this._poiLayersByKey.delete(key)
    })

    const numberedMarkers = this.shouldUseNumberedMarkers(nextPois.length)
    nextPois.forEach((poi, idx) => {
      if (!poi || !poi.lat || !poi.lon) return
      const key = this.poiKeyFor(poi, idx)
      if (this._poiLayersByKey.has(key)) return

      const marker = this.createPoiMarker(poi, idx, currentPoiIndex, numberedMarkers)
      this._poiLayersByKey.set(key, marker)
    })

    const poiLatLngs = nextPois
      .filter((poi) => poi && poi.lat && poi.lon)
      .map((poi) => [poi.lat, poi.lon])
    const fitLatLngs = this.routeAndPoiFitLatLngs(poiLatLngs, nextPois)
    this.fitMapToLatLngs(fitLatLngs)
  }

  buildNumberedPoiIcon(idx, currentPoiIndex) {
    const idxDisplay = this.itineraryOverviewValue ? Number(idx) + 1 : (Number(idx) === 0 ? "D" : idx)
    const active = Number(idx) === Number(currentPoiIndex)
    const backgroundColor = active ? "#1f4fb4" : (this.itineraryOverviewValue ? "#ffffff" : "#2b6cee")
    const textColor = active || !this.itineraryOverviewValue ? "#ffffff" : "#2b6cee"
    const iconHtml = `<div style="
      width:30px;height:30px;
      background: ${backgroundColor};
      color: ${textColor};
      border-radius:50%;
      display:flex;
      align-items:center;
      justify-content:center;
      font-weight:700;
      font-size:12px;
      border:3px solid ${this.itineraryOverviewValue ? "#ffffff" : "#e0e7ff"};
      box-shadow:0 3px 10px rgba(16,32,74,0.3), 0 0 0 3px rgba(43,108,238,0.12);
      opacity:0.98;
    "><p style="margin:0;line-height:1">${idxDisplay}</p></div>`
    return L.divIcon({
      html: iconHtml,
      className: "poi-circle-icon",
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    })
  }

  buildOverviewPoiIcon(idx) {
    if (Number(idx) === 0) {
      return L.divIcon({
        html: `<div style="
          width:24px;height:24px;border-radius:50%;
          background:#0f766e;border:3px solid #ffffff;
          box-shadow:0 3px 10px rgba(15,118,110,0.38), 0 0 0 3px rgba(15,118,110,0.12);
        "></div>`,
        className: "poi-overview-start-icon",
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      })
    }

    if (Number(idx) === 1) {
      return L.divIcon({
        html: `<div style="
          width:21px;height:21px;border-radius:50%;
          background:#1f4fb4;border:3px solid #ffffff;
          box-shadow:0 3px 10px rgba(31,79,180,0.38), 0 0 0 3px rgba(43,108,238,0.1);
        "></div>`,
        className: "poi-overview-first-stop-icon",
        iconSize: [21, 21],
        iconAnchor: [10.5, 10.5]
      })
    }

    return this.buildDotPoiIcon()
  }

  buildDotPoiIcon() {
    const iconHtml = `<div style="
      width:13px;height:13px;
      background:#2b6cee;
      border-radius:50%;
      border:2px solid #ffffff;
      opacity:0.92;
      box-shadow:0 2px 7px rgba(43,108,238,0.3), 0 0 0 3px rgba(43,108,238,0.12);
    "></div>`
    return L.divIcon({
      html: iconHtml,
      className: "poi-dot-icon",
      iconSize: [13, 13],
      iconAnchor: [6.5, 6.5]
    })
  }

  createPoiMarker(poi, idx, currentPoiIndex, numberedMarkers) {
    const icon = numberedMarkers
      ? this.buildNumberedPoiIcon(idx, currentPoiIndex)
      : this.buildOverviewPoiIcon(idx)

    const marker = L.marker([poi.lat, poi.lon], { icon }).addTo(this.map)
    marker.bindPopup(this.poiTooltipLabel(poi, idx), {
      closeButton: false,
      autoPan: true,
      maxWidth: 220,
      className: "map-poi-popup"
    })
    return marker
  }

  poiTooltipLabel(poi, idx) {
    const safeName = escapeHtml(poi?.name || "Lieu")
    const poiId = Number(poi?.id)
    const safePhotoUrl = typeof poi?.photo_url === "string" && /^https?:\/\//i.test(poi.photo_url)
      ? escapeHtml(poi.photo_url)
      : null
    const image = safePhotoUrl
      ? `<img src="${safePhotoUrl}" alt="Photo de ${safeName}" style="width:100%;height:110px;object-fit:cover;border-radius:8px;margin-bottom:8px;">`
      : ""
    const label = this.itineraryOverviewValue
      ? `Étape ${Number(idx) + 1}`
      : (Number(idx) === 0 ? "Départ" : `Étape ${idx}`)
    const playButton = Number.isInteger(poiId) && poiId > 0
      ? `<a href="/pois/${poiId}" data-turbo-frame="scene" data-turbo-action="advance" aria-label="Lire ${safeName}" style="display:flex;align-items:center;justify-content:center;width:56px;height:56px;margin:12px auto 0;background:linear-gradient(to right,#2563eb,#1e40af);color:#fff;border-radius:50%;text-decoration:none;box-shadow:0 10px 15px -3px rgba(191,219,254,0.9);transition:transform .2s,background .2s;"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" style="width:28px;height:28px;"><path stroke-linecap="round" stroke-linejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z" /></svg></a>`
      : ""

    return `<div style="min-width:172px;padding:2px 1px 1px;color:#111318;">${image}<div style="font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#4338ca;font-weight:700;margin-bottom:3px;">${label}</div><div style="font-size:14px;font-weight:700;line-height:1.25;">${safeName}</div>${playButton}</div>`
  }

  addUserLocation() {
    if (!navigator.geolocation) return
    this._userMarker = null
    this._userCircle = null
    this._geoWatchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords
        const latlng = [latitude, longitude]
        if (!this._userMarker) {
          const userIcon = L.divIcon({
            html: `<div style="width:18px;height:18px;background:#2196f3;border-radius:50%;border:3px solid #fff;box-shadow:0 0 8px #2196f3;position:relative;"></div>`,
            className: "",
            iconSize: [18, 18],
            iconAnchor: [9, 9]
          })
          this._userMarker = L.marker(latlng, { icon: userIcon }).addTo(this.map)
        } else {
          this._userMarker.setLatLng(latlng)
        }

        if (!this._userCircle) {
          this._userCircle = L.circle(latlng, {
            radius: accuracy,
            color: "#2196f3",
            fillColor: "#2196f3",
            fillOpacity: 0.15,
            weight: 1
          }).addTo(this.map)
        } else {
          this._userCircle.setLatLng(latlng)
          this._userCircle.setRadius(accuracy)
        }
      },
      (error) => {
        console.warn("[map_controller] Geolocation error:", error)
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    )
  }

  addDynamicLayer(layer) {
    this._dynamicLayers.push(layer)
    return layer
  }

  clearDynamicLayers() {
    this._dynamicLayers.forEach((layer) => {
      if (this.map && this.map.hasLayer(layer)) this.map.removeLayer(layer)
    })
    this._dynamicLayers = []
    this._routeLatLngs = []
  }

  applyMapPatch(event) {
    if (!event?.detail || !this.map) return
    if (!this.element.closest("#loading-map")) return

    const { phase, pois, geometry } = event.detail
    const nextPoisValue = JSON.stringify(Array.isArray(pois) ? pois : [])
    const nextGeojsonValue = geometry ? JSON.stringify(geometry) : ""
    const currentPoisValue = this.hasPoisValue && this.poisValue ? this.poisValue : ""
    const currentGeojsonValue = this.hasGeojsonValue && this.geojsonValue ? this.geojsonValue : ""
    const poisChanged = nextPoisValue !== currentPoisValue
    const geometryChanged = nextGeojsonValue !== currentGeojsonValue

    this.phaseValue = phase || this.phaseValue

    if (!poisChanged && !geometryChanged) return

    const nextPois = Array.isArray(pois) ? pois : []
    const shouldPatchPoisOnly =
      poisChanged &&
      !geometryChanged &&
      !currentGeojsonValue &&
      !nextGeojsonValue

    if (shouldPatchPoisOnly) {
      this.poisValue = nextPoisValue
      this.patchPoisInPlace(nextPois, this.currentPoiIndexValue)
      return
    }

    this.clearTimers()
    this.clearDynamicLayers()
    this.clearPoiLayers()

    this.poisValue = nextPoisValue
    if (geometry) {
      this.geojsonValue = nextGeojsonValue
    } else if (this.hasGeojsonValue) {
      this.geojsonValue = ""
    }

    this.addPhaseScanLayer(this.startLatValue, this.startLonValue)
    this.renderRouteIfAny()
    this.renderPois(this.currentPoiIndexValue)
  }

}

function extractLineCoords(geojson) {
  if (geojson.type === "LineString") return geojson.coordinates.map((c) => [c[1], c[0]])
  if (geojson.type === "Feature" && geojson.geometry.type === "LineString") {
    return geojson.geometry.coordinates.map((c) => [c[1], c[0]])
  }
  if (geojson.type === "FeatureCollection") {
    for (const feat of geojson.features) {
      if (feat.geometry.type === "LineString") return feat.geometry.coordinates.map((c) => [c[1], c[0]])
    }
  }
  return []
}

function buildArrowHead(pixelSize = 10, fillColor = "#2196f3", strokeColor = "#fff") {
  return L.Symbol.arrowHead({
    pixelSize: pixelSize,
    polygon: true,
    pathOptions: {
      stroke: true,
      color: strokeColor,
      weight: 1.5,
      opacity: 1,
      fillColor: fillColor,
      fillOpacity: 1
    }
  })
}

function findClosestIndex(coords, target) {
  let minDist = Infinity
  let minIdx = 0
  coords.forEach((c, i) => {
    const d = Math.pow(c[0] - target[0], 2) + Math.pow(c[1] - target[1], 2)
    if (d < minDist) {
      minDist = d
      minIdx = i
    }
  })
  return minIdx
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;")
    .replaceAll("'", "&#39;")
}
